import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { 
  FaRocket, 
  FaChartLine, 
  FaBullseye, 
  FaUsers, 
  FaBullhorn, // Changed from FaMegaphone
  FaHandshake 
} from 'react-icons/fa';

const BrandLaunch = () => {
  const features = [
    {
      icon: <FaRocket />,
      title: "Strategic Launch Planning",
      description: "Comprehensive launch strategy tailored to your brand's unique positioning and target market"
    },
    {
      icon: <FaChartLine />,
      title: "Market Analysis",
      description: "In-depth market research and competitor analysis to identify opportunities and challenges"
    },
    {
      icon: <FaBullseye />,
      title: "Audience Targeting",
      description: "Precise audience segmentation and targeting strategies for maximum impact"
    },
    {
      icon: <FaUsers />,
      title: "Community Building",
      description: "Build and engage with your brand community from day one"
    },
    {
      icon: <FaBullhorn />, // Changed from FaMegaphone
      title: "Multi-Channel Promotion",
      description: "Coordinated promotion across social media, PR, and digital platforms"
    },
    {
      icon: <FaHandshake />,
      title: "Partnership Development",
      description: "Strategic partnerships and collaborations to amplify your launch"
    }
  ];

  useEffect(() => {
    AOS.init({ duration: 1000, once: true, easing: 'ease-out' });
  }, []);

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mt-24 mb-16" data-aos="fade-up">
          <div className="relative inline-block">
            <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              Brand Launch Services
            </h1>
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"></div>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mt-8 leading-relaxed">
            Transform your brand vision into reality with our comprehensive launch services. 
            We combine strategic planning, market insights, and creative execution to ensure 
            a successful brand launch.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              data-aos="fade-up"
              data-aos-delay={index * 100}
              className="bg-white rounded-xl p-6 transform hover:-translate-y-2 transition-all duration-300 hover:shadow-xl border border-blue-100"
            >
              <div className="text-blue-600 mb-6 bg-white p-4 rounded-lg inline-block shadow-md text-3xl flex justify-center">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-2 text-center">{feature.title}</h3>
              <p className="text-gray-600 text-center">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Process Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-16" data-aos="fade-up">
          <h2 className="text-3xl font-bold text-center mb-12">Our Launch Process</h2>
          <div className="space-y-8">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <div className="bg-blue-100 text-blue-600 rounded-full p-4 w-16 h-16 flex items-center justify-center font-bold text-xl">1</div>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Discovery & Strategy</h3>
                <p className="text-gray-600 mb-4">Understanding your brand vision and developing a tailored launch strategy</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <div className="bg-blue-100 text-blue-600 rounded-full p-4 w-16 h-16 flex items-center justify-center font-bold text-xl">2</div>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Planning & Preparation</h3>
                <p className="text-gray-600 mb-4">Creating detailed timelines, content calendars, and marketing materials</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <div className="bg-blue-100 text-blue-600 rounded-full p-4 w-16 h-16 flex items-center justify-center font-bold text-xl">3</div>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Launch Execution</h3>
                <p className="text-gray-600 mb-4">Coordinated implementation across all channels and platforms</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <div className="bg-blue-100 text-blue-600 rounded-full p-4 w-16 h-16 flex items-center justify-center font-bold text-xl">4</div>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Monitoring & Optimization</h3>
                <p className="text-gray-600 mb-4">Real-time tracking and adjustments for maximum impact</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div 
          className="relative overflow-hidden rounded-2xl mb-20"
          data-aos="fade-up"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-90"></div>
          <div className="relative z-10 p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-6">Ready to Launch Your Brand?</h2>
            <p className="text-xl mb-8 opacity-90">Let's create a memorable launch that sets your brand up for success</p>
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-300 shadow-lg">
              Get Started
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandLaunch;

